import { dia } from '@joint/core'
import { updateRelationship, getExistingRelationships, parseCardinality, autoLinkObjects, autoLinkAllObjects, createAutoLinkVisualization } from '../../../map-editor-features/editor/lib/relationshipUtils'
import { isZoneCctvRelation, linkZonesByCctvId, ZONE_CCTV_ID_RELATION } from '../../../map-editor-features/editor/lib/zoneCctvIdRelations'
import { involvesGuideBoard } from '../../../map-editor-features/editor/lib/guideBoardDirectionRelations'
import type { Template, TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'
import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import type { RelationshipEditState } from '../types'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'

interface UseRelationshipManagementProps {
  graph: dia.Graph | null
  paper: dia.Paper | null
  selectedElementId: string | null
  template: Template | null
  types: ObjectType[]
  mutableRelationTypes: Record<string, TemplateRelationType>
  setMutableRelationTypes: React.Dispatch<React.SetStateAction<Record<string, TemplateRelationType>>>
  handleObjectUpdate: (id: string, updates: Partial<any>) => void
  relationshipEditState: RelationshipEditState
  setRelationshipEditState: React.Dispatch<React.SetStateAction<RelationshipEditState>>
}

interface UseRelationshipManagementReturn {
  handleUpdateRelationType: (key: string, config: TemplateRelationType) => void
  handleDeleteRelationType: (key: string) => void
  handleUnlink: (relationKey: string, targetId: string) => void
  handleAutoLink: (relationKey: string) => void
  handleRelationEditModeChange: (editing: boolean, relationKey: string | null, availableTargetIds: string[]) => void
  handleAutoLinkConfirm: (adjustedDistances: Record<string, number>, enabledRelations: Record<string, boolean>) => Promise<void>
}

/**
 * Custom hook for managing object relationships in the map editor
 * Handles relationship CRUD operations, auto-linking, and relationship editing state
 */
/** 미연결 목록은 자동 관계 생성 모달이 적용 전에 전부 보여준다. 여기서 alert 로
 *  앞 10대만 잘라 띄우면 나머지는 어디에서도 확인할 수 없다. */
function logZoneResult(result: { linked: number; unmatched: string[] } | null): void {
  if (!result) return
  log.info(LogCategory.GENERAL, 'zone-cctv by id', {
    linked: result.linked,
    unmatched: result.unmatched,
  })
}

export function useRelationshipManagement({
  graph,
  paper,
  selectedElementId,
  template,
  types,
  mutableRelationTypes,
  setMutableRelationTypes,
  handleObjectUpdate,
  relationshipEditState,
  setRelationshipEditState,
}: UseRelationshipManagementProps): UseRelationshipManagementReturn {

  // Relation Type Management Handlers
  const handleUpdateRelationType = (key: string, config: TemplateRelationType) => {
    setMutableRelationTypes(prev => ({
      ...prev,
      [key]: config
    }))
  }

  const handleDeleteRelationType = (key: string) => {
    setMutableRelationTypes(prev => {
      const updated = { ...prev }
      delete updated[key]
      return updated
    })
  }

  // Relationship Handlers
  const handleUnlink = (relationKey: string, targetId: string) => {
    if (!selectedElementId || !graph) return

    const element = graph.getCell(selectedElementId) as dia.Element
    if (!element) return

    const relationConfig = mutableRelationTypes[relationKey]
    if (!relationConfig) {
      return
    }


    const currentData = element.get('data') || {}

    // Capture the updated data from updateRelationship
    const newData = updateRelationship(
      element,
      relationConfig.propertyKey,
      targetId,
      relationConfig.cardinality,
      'remove'
    )


    // Force a re-render by triggering a change event
    element.trigger('change:data', element, newData)

    // Trigger update to refresh UI with the new data (this will update dataVersion)
    handleObjectUpdate(selectedElementId, { data: newData })
  }

  const handleAutoLink = (relationKey: string) => {
    if (!selectedElementId || !graph) return

    const element = graph.getCell(selectedElementId) as dia.Element
    if (!element) return

    const relationConfig = mutableRelationTypes[relationKey]
    if (!relationConfig) return
    if (isZoneCctvRelation(relationConfig.sourceType, relationConfig.targetType)) {
      const result = linkZonesByCctvId(graph, mutableRelationTypes, selectedElementId)
      handleObjectUpdate(selectedElementId, { data: element.get('data') })
      logZoneResult(result)
      return
    }
    if (!relationConfig.autoLink) return


    // Get existing relationships
    const existingTargets = getExistingRelationships(element, relationConfig.propertyKey)
    const existingCount = existingTargets.length


    // Calculate max links
    const maxLinks = parseCardinality(relationConfig.cardinality)


    // Auto-link with capacity limit
    const linkedIds = autoLinkObjects(
      graph,
      element,
      relationKey,
      relationConfig,
      undefined,
      maxLinks ?? undefined
    )


    if (linkedIds.length === 0) {
      alert('범위 내에서 연결 가능한 객체를 찾지 못했습니다.')
      return
    }

    // Clear existing relationships first
    let currentData = element.get('data')

    if (existingCount > 0) {

      existingTargets.forEach(targetId => {
        currentData = updateRelationship(
          element,
          relationConfig.propertyKey,
          targetId,
          relationConfig.cardinality,
          'remove'
        )
      })
    }

    // Now add the new relationships one by one

    linkedIds.forEach((targetId, index) => {

      currentData = updateRelationship(
        element,
        relationConfig.propertyKey,
        targetId,
        relationConfig.cardinality,
        'add'
      )

    })

    // Single update at the end (this will update dataVersion)
    handleObjectUpdate(selectedElementId, { data: currentData })


    const message = existingCount > 0
      ? `기존 ${existingCount}개 관계를 삭제하고 ${linkedIds.length}개 새 관계를 생성했습니다.`
      : `${linkedIds.length}개 관계를 자동으로 생성했습니다.`

  }

  // Handle relationship edit mode change from RelationshipManager
  const handleRelationEditModeChange = (
    editing: boolean,
    relationKey: string | null,
    availableTargetIds: string[]
  ) => {

    // Get property key from relationKey
    let propertyKey: string | null = null
    if (relationKey && mutableRelationTypes[relationKey]) {
      propertyKey = mutableRelationTypes[relationKey].propertyKey
    }

    setRelationshipEditState({
      editing,
      relationKey,
      propertyKey,
      targetIds: availableTargetIds
    })
  }

  const handleAutoLinkConfirm = async (
    adjustedDistances: Record<string, number>,
    enabledRelations: Record<string, boolean>
  ) => {
    if (!graph || !paper) return

    // Filter out disabled relation types. 안내현황판 관계는 반경이 아니라 좌/우
    // 영역 드래그로만 맺으므로 반경 자동관계 실행에서 제외한다.
    const activeRelationTypes = Object.fromEntries(
      Object.entries(mutableRelationTypes).filter(
        ([key, config]) =>
          enabledRelations[key] !== false &&
          !involvesGuideBoard(config.sourceType, config.targetType) &&
          !isZoneCctvRelation(config.sourceType, config.targetType)
      )
    )

    const enabledCount = Object.keys(activeRelationTypes).length
    const totalCount = Object.keys(mutableRelationTypes).length

    // Run auto-link in next frame to allow UI to show loading state
    const results = await new Promise<ReturnType<typeof autoLinkAllObjects>>(resolve => {
      setTimeout(() => {
        resolve(autoLinkAllObjects(graph, activeRelationTypes, types, template, adjustedDistances))
      }, 0)
    })

    const zoneResult = enabledRelations[ZONE_CCTV_ID_RELATION]
      ? linkZonesByCctvId(graph, mutableRelationTypes)
      : null
    logZoneResult(zoneResult)

    const allResults = results

    if (allResults.length > 0) {
      const cleanup = createAutoLinkVisualization(paper, graph, allResults)
      setTimeout(() => cleanup(), 5000)

      const totalLinks = allResults.reduce((sum, r) => sum + r.targetIds.length, 0)
      const disabledCount = totalCount - enabledCount
      const _message = disabledCount > 0
        ? `${totalLinks}개 관계를 생성했습니다. (활성화: ${enabledCount}개 / 비활성화: ${disabledCount}개)`
        : `${totalLinks}개 관계를 생성했습니다.`
    } else if (!zoneResult?.linked && !zoneResult?.unmatched.length) {
      alert('생성된 관계가 없습니다. 객체와 타입을 확인하세요.')
    }
  }

  return {
    handleUpdateRelationType,
    handleDeleteRelationType,
    handleUnlink,
    handleAutoLink,
    handleRelationEditModeChange,
    handleAutoLinkConfirm,
  }
}
